import React, { useState, useEffect, Fragment } from "react";
import {
  BrowserRouter as Router,
  Switch,
  Route,
  Link,
  useHistory,
  Redirect,
  useLocation,
  useParams,
} from "react-router-dom";
import "./index.scss";
import { useTranslation } from "react-i18next";
import "react-loading-skeleton/dist/skeleton.css";
import { useSelector } from "react-redux";
import Host from "../../Components/Host";
import { useGetUserDataQuery } from "../../services/login";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PhoneIcon from "@mui/icons-material/Phone";
import HelmetComponent from "../../Components/HelmetComponent";

function ReturnsRefund() {
  const {
    userId,
    userToken,
    userEmail,
    userName,
    isMember,
    isPaymentMethod,
    languageCode,
    countryCode,
  } = useSelector((state) => state.auth);
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();
  const [getSearchId, setSearchId] = useState("");

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      // behavior: "smooth"
    });
  }, []);

  const { data: userData } = useGetUserDataQuery(
    { userId: userId, userToken: userToken, countryCode: countryCode },
    { count: 1 },
    { refetchOnMountOrArgChange: true },
  );

  const myElementRef = React.useRef(null);
  const myElementRefs = React.useRef(null);
  const myContact = React.useRef(null);

  const myPayment = React.useRef(null);
  const myBilling = React.useRef(null);

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.substring(1);
      if (hash === "returns") {
        myElementRef.current.scrollIntoView({ behavior: "smooth" });
      }
      if (hash === "shipping") {
        myElementRefs.current.scrollIntoView({ behavior: "smooth" });
      }
      if (hash === "contact") {
        myContact.current.scrollIntoView({ behavior: "smooth" });
      }
      if (hash === "billing") {
        myBilling.current.scrollIntoView({ behavior: "smooth" });
      }
      if (hash === "payment") {
        myPayment.current.scrollIntoView({ behavior: "smooth" });
      }
    };
    window.addEventListener("hashchange", handleHashChange);
    handleHashChange();
    return () => {
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, [window, window.location.hash]);

  return (
    <>
      <HelmetComponent title="Return refunds" metaTitle={Host?.metaTitle} />
      <div className="statisc-page">
        <div className="container static-container">
          <div className="row static-row">
            <div className="col-md-10">
              <div
                className="static-container-card"
                style={{
                  textAlign: "left",
                  padding: "20px",
                  background: "#fcfcfc",
                  border: "1px solid #eee",
                  borderRadius: "7px",
                }}
              >
                <h1
                  style={{
                    padding: "20px 0px 20px 0px",
                    fontWeight: 300,
                    fontSize: "1.4rem",
                  }}
                >
                  {t("Returns, Warranty and Refunds")}
                </h1>

                <p>
                  At {Host?.domainName}, we want customers to shop with
                  confidence. This policy explains how returns, refunds,
                  cancellations, damaged items, defective items, incorrect
                  items, and warranty claims are handled for orders placed on{" "}
                  {window.location.hostname}.
                </p>
                <p>
                  Please read this policy carefully before placing an order. By
                  placing an order on our website, you agree to the terms below.
                </p>

                <h4>1. Return Window</h4>
                <p>
                  Customers may request a return within 7 days from the date of
                  delivery only for eligible reasons listed in this policy.
                </p>
                <p>To be eligible for a return, the product must be:</p>
                <p style={{ marginBottom: "5px", textTransform: "capitalize" }}>
                  • unused, unless the issue is a verified defect;
                </p>
                <p style={{ marginBottom: "5px", textTransform: "capitalize" }}>
                  • in its original packaging;
                </p>
                <p style={{ marginBottom: "5px", textTransform: "capitalize" }}>
                  • complete with all accessories, manuals, tags, labels,
                  warranty cards, and invoices;
                </p>
                <p style={{ marginBottom: "5px", textTransform: "capitalize" }}>
                  • not damaged, altered, or missing any parts due to customer
                  handling.
                </p>
                <p>Returned products will be inspected before approval.</p>

                <h4>2. Items Eligible for Return</h4>
                <p>A return may be accepted only if:</p>
                <p style={{ marginBottom: "5px", textTransform: "capitalize" }}>
                  • the wrong item was delivered;
                </p>
                <p style={{ marginBottom: "5px", textTransform: "capitalize" }}>
                  • the item arrived damaged;
                </p>
                <p style={{ marginBottom: "5px", textTransform: "capitalize" }}>
                  • the item is defective;
                </p>
                <p style={{ marginBottom: "5px", textTransform: "capitalize" }}>
                  • the item does not match the product description on our
                  website;
                </p>
                <p style={{ marginBottom: "5px", textTransform: "capitalize" }}>
                  • the item is eligible for warranty support, where applicable.
                </p>
                <p>Returns are subject to inspection and approval.</p>

                <h4>3. Change of Mind Returns</h4>
                <p>
                  We do not accept returns, refunds, or exchanges for change of
                  mind, buyer's remorse, incorrect selection by the customer, or
                  products ordered by mistake.
                </p>
                <p>
                  Customers are requested to review product details,
                  specifications, size, compatibility, delivery timelines,
                  warranty information, and pricing carefully before placing an
                  order.
                </p>
                <p>
                  This does not affect the customer's right to request support
                  if the item received is damaged, defective, incorrect, or not
                  as described.
                </p>

                <h4>4. Items Not Eligible for Return</h4>
                <p>
                  For hygiene, safety, legal, or product-condition reasons, some
                  items may not be eligible for return, including:
                </p>
                <p>
                  • products returned due to change of mind, buyer's remorse,
                  incorrect selection by the customer, or products ordered by
                  mistake;
                </p>
                <p style={{ marginBottom: "5px", textTransform: "capitalize" }}>
                  • used, damaged, or opened products that cannot be resold,
                  except where the issue is a verified defect;
                </p>
                <p style={{ marginBottom: "5px", textTransform: "capitalize" }}>
                  • products missing original packaging, accessories, manuals,
                  tags, labels, warranty cards, or invoices;
                </p>
                <p style={{ marginBottom: "5px", textTransform: "capitalize" }}>
                  • personal care, hygiene, beauty, grooming, or health-related
                  items once opened;
                </p>
                <p style={{ marginBottom: "5px", textTransform: "capitalize" }}>
                  • consumable products once opened;
                </p>
                <p style={{ marginBottom: "5px", textTransform: "capitalize" }}>
                  • software, digital products, gift cards, or downloadable
                  items;
                </p>
                <p style={{ marginBottom: "5px", textTransform: "capitalize" }}>
                  • products damaged due to misuse, improper handling,
                  installation error, or unauthorized repair;
                </p>
                <p style={{ marginBottom: "5px", textTransform: "capitalize" }}>
                  • items marked as Final Sale, Non-Returnable, or Clearance,
                  where clearly mentioned on the product page.
                </p>
                <p>
                  This does not affect the customer's right to request support
                  for damaged, defective, incorrect, or not-as-described
                  products.
                </p>

                <h4>
                  5. Damaged, Defective, Incorrect, or Not-as-Described Items
                </h4>
                <p>
                  If your item is delivered damaged, defective, incorrect, or
                  not as described, please contact us within 3 days of delivery.
                </p>
                <p>Please provide:</p>
                <p style={{ marginBottom: "5px", textTransform: "capitalize" }}>
                  • order number;
                </p>
                <p style={{ marginBottom: "5px", textTransform: "capitalize" }}>
                  • clear photos or video of the product;
                </p>
                <p style={{ marginBottom: "5px", textTransform: "capitalize" }}>
                  • photos of the packaging;
                </p>
                <p style={{ marginBottom: "5px", textTransform: "capitalize" }}>
                  • a description of the issue.
                </p>
                <p>
                  After reviewing the request, we may arrange a return pickup,
                  replacement, repair, or refund, depending on the product
                  condition, inspection result, warranty coverage, and
                  availability.
                </p>
                <p>
                  Claims reported after 3 days may still be reviewed, but
                  approval will depend on the product type, issue, available
                  evidence, and inspection result.
                </p>

                <h4>6. Return Process</h4>
                <p>To request a return, contact our support team:</p>
                <p>Email: {Host?.email}</p>
                <p>Phone: {Host?.phone}</p>
                <p>
                  Support Hours: 8:00 AM to 10:00 PM, Dubai Time, 7 days a week
                </p>
                <p>
                  Please include your order number and reason for return. Once
                  your return request is reviewed, our team will confirm whether
                  the item is eligible and provide return instructions.
                </p>
                <p>
                  Do not send any item back without return approval. Unapproved
                  returns may not be accepted.
                </p>

                <h4>7. Return Shipping Fees</h4>
                <p>
                  Return shipping charges depend on the reason for the return:
                </p>
                <p style={{ marginBottom: "5px", textTransform: "capitalize" }}>
                  • Wrong item delivered — Paid by {Host?.domainName} after
                  verification
                </p>
                <p style={{ marginBottom: "5px", textTransform: "capitalize" }}>
                  • Damaged item delivered — Paid by {Host?.domainName} after
                  verification
                </p>
                <p style={{ marginBottom: "5px", textTransform: "capitalize" }}>
                  • Defective item — Paid by {Host?.domainName} after
                  verification
                </p>
                <p style={{ marginBottom: "5px", textTransform: "capitalize" }}>
                  • Item not as described — Paid by {Host?.domainName} after
                  verification
                </p>
                <p style={{ marginBottom: "5px", textTransform: "capitalize" }}>
                  • Change of mind / ordered by mistake — Not eligible for
                  return
                </p>
                <p style={{ marginBottom: "5px", textTransform: "capitalize" }}>
                  • Product not in original condition — Return may be rejected
                </p>
                <p>
                  If a returned item is found to be used, damaged by the
                  customer, missing parts, or not eligible under this policy,
                  the return may be rejected and the item may be sent back to
                  the customer.
                </p>

                <h4>8. Refunds</h4>
                <p>
                  Once the returned item is received and inspected, we will
                  notify the customer whether the refund has been approved or
                  rejected.
                </p>
                <p>
                  If approved, the refund will be processed to the original
                  payment method where possible.
                </p>
                <p>
                  Refunds are usually processed within 7 to 15 business days
                  after return approval. The actual time for the amount to
                  appear in the customer's account may depend on the bank, card
                  provider, or payment service provider.
                </p>
                <p>
                  Original shipping charges, cash-on-delivery charges, service
                  fees, or payment processing charges may be non-refundable
                  unless the return is due to our error, a damaged item,
                  defective item, incorrect item, or item not as described.
                </p>

                <h4>9. Replacements</h4>
                <p>
                  Where available, customers may request a replacement instead
                  of a refund.
                </p>
                <p>
                  A replacement may be offered if the same item is available,
                  the issue is verified, and the original item is returned in
                  accordance with this policy.
                </p>
                <p>
                  If a replacement is not available, we may offer a refund or an
                  alternative product option.
                </p>

                <h4>10. Order Cancellations</h4>
                <p>
                  Customers may request cancellation before the order has been
                  shipped or processed for dispatch.
                </p>
                <p>
                  Once an order has been shipped, the cancellation request may
                  be treated as a return request and will be subject to this
                  policy.
                </p>
                <p>
                  If an item becomes unavailable after an order is placed, we
                  will notify the customer and offer one of the following
                  options:
                </p>
                <p style={{ marginBottom: "5px", textTransform: "capitalize" }}>
                  • wait for availability;
                </p>
                <p style={{ marginBottom: "5px", textTransform: "capitalize" }}>
                  • choose an alternative product;
                </p>
                <p style={{ marginBottom: "5px", textTransform: "capitalize" }}>
                  • cancel the item and receive a full refund for the
                  unavailable item.
                </p>

                <h4>11. Product Availability and Pricing</h4>
                <p>
                  We make reasonable efforts to keep product availability,
                  prices, and delivery estimates accurate and updated.
                </p>
                <p>
                  In rare cases, an item may become unavailable due to
                  supplier-side stock changes, system updates, or fulfillment
                  limitations. If this happens after an order is placed, we will
                  contact the customer promptly and provide available options,
                  including a full refund for the affected item.
                </p>
                <p>
                  Customers will not be charged any additional amount without
                  confirmation.
                </p>

                <h4>12. Warranty</h4>
                <p>
                  Some products may include a manufacturer warranty, seller
                  warranty, or supplier warranty. Warranty coverage depends on
                  the product type and the warranty information shown on the
                  product page or included with the item.
                </p>
                <p>Warranty does not usually cover:</p>
                <p style={{ marginBottom: "5px", textTransform: "capitalize" }}>
                  • physical damage;
                </p>
                <p style={{ marginBottom: "5px", textTransform: "capitalize" }}>
                  • water damage;
                </p>
                <p style={{ marginBottom: "5px", textTransform: "capitalize" }}>
                  • misuse or negligence;
                </p>
                <p style={{ marginBottom: "5px", textTransform: "capitalize" }}>
                  • unauthorized repair;
                </p>
                <p style={{ marginBottom: "5px", textTransform: "capitalize" }}>
                  • normal wear and tear;
                </p>
                <p style={{ marginBottom: "5px", textTransform: "capitalize" }}>
                  • missing accessories or packaging;
                </p>
                <p style={{ marginBottom: "5px", textTransform: "capitalize" }}>
                  • damage caused by incorrect installation or handling.
                </p>
                <p>
                  For warranty support, customers should contact us with the
                  order number, product details, and issue description.
                </p>

                <h4>13. Refunds for Cash on Delivery Orders</h4>
                <p>
                  For Cash on Delivery orders, approved refunds may be issued
                  through bank transfer, store credit, wallet credit, or another
                  available refund method confirmed by our support team.
                </p>
                <p>
                  Customers may be required to provide valid bank or payment
                  details to complete the refund.
                </p>

                <h4>14. Inspection and Approval</h4>
                <p>All returns are subject to inspection.</p>
                <p>
                  {Host?.domainName} reserves the right to reject a return if:
                </p>
                <p style={{ marginBottom: "5px", textTransform: "capitalize" }}>
                  • the item is not in original condition;
                </p>
                <p style={{ marginBottom: "5px", textTransform: "capitalize" }}>
                  • the item has been used, damaged, or altered by the customer;
                </p>
                <p style={{ marginBottom: "5px", textTransform: "capitalize" }}>
                  • accessories, packaging, manuals, warranty cards, or invoices
                  are missing;
                </p>
                <p style={{ marginBottom: "5px", textTransform: "capitalize" }}>
                  • the return request is outside the allowed return period;
                </p>
                <p style={{ marginBottom: "5px", textTransform: "capitalize" }}>
                  • the product is listed as non-returnable;
                </p>
                <p style={{ marginBottom: "5px", textTransform: "capitalize" }}>
                  • the issue is not verified after inspection.
                </p>

                <h4>15. Contact Information</h4>
                <p>
                  For returns, refunds, cancellations, or warranty support,
                  please contact:
                </p>
                <p style={{ lineHeight: 2 }}>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: Host?.address,
                    }}
                  ></div>
                  <MailOutlineIcon style={{ fontSize: "18px" }} /> {Host?.email}{" "}
                  <br />
                  <PhoneIcon style={{ fontSize: "18px" }} /> {Host?.phone}
                </p>
                <p>Website: {window.location.hostname}</p>
                <p>
                  Support Hours: 8:00 AM to 10:00 PM, Dubai Time, 7 days a week
                </p>

                <p>&nbsp;</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default ReturnsRefund;
